import { readToplineReportingWindow } from "../platformAnalytics/toplineReportingWindow";
// client/src/pages/SocialAnalytics.js
// ✅ Update: Added Amazon tile AFTER Reddit using amazon.jpg and routing to /amazon-analytics

import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import "./Dashboard.css";
import "./SocialAnalytics.css";

import AnalyticsReportViewer from "../components/AnalyticsReportViewer";
import CphereHeaderLogo from "../components/CphereHeaderLogo";
import {
  AdvertiserIdentityTrigger,
  GovernedAdvertiserSelectorModal,
} from "../components/GovernedAdvertiserSelector";
import Loading from "../components/Loading";
import facebookLogo from "../assets/facebook.jpg";
import instagramLogo from "../assets/instagram.jpg";
import metaLogo from "../assets/meta.jpg";
import tiktokLogo from "../assets/tiktok.jpg";
import youtubeLogo from "../assets/youtube.jpg";
import xLogo from "../assets/x.jpg";
import truthsocialLogo from "../assets/truth-social-logo.jpg";
import rumbleLogo from "../assets/rumble-logo.jpg";
import googleLogo from "../assets/google.jpg";
import openaiLogo from "../assets/openAI.png";
import redditLogo from "../assets/reddit.jpg"; // ✅ Reddit
import amazonLogo from "../assets/amazon.jpg"; // ✅ Amazon (NEW)
import yahooLogo from "../assets/yahoo.png";
import shopifyLogo from "../assets/shopify.png";
import linkedinLogo from "../assets/linkedin.png";
import communityManagementImage from "../assets/communitymanagement.png"; // ✅ Community (Manager)
import campaignManagementImage from "../assets/campaignmanagement.jpg";
import reportLoadingIcon from "../assets/reporticonLAST.png";
import reverseArrowImage from "../assets/reversearrow.jpg";

import { useNavigate } from "react-router-dom";
import useGovernedAdvertiserCatalog from "../hooks/useGovernedAdvertiserCatalog";
import usePageAwareness from "../hooks/usePageAwareness";
import useResolvedAnalyticsTimeframe from "../hooks/useResolvedAnalyticsTimeframe";
import { getToplinePerformance } from "../services/analyticsToplineService";
import { normalizeAdvertiserVertical } from "../config/verticalDashboardConfig";
import { normalizeAnalyticsEmbedUrl } from "../components/analyticsReportUtils";
import { aggregateCalendarQualification, reportingTimeZoneStatement, sourceCalendarQualification } from "../utils/reportingCalendar";
import {
  SOCIAL_INTELLIGENCE_RANKING_CONTEXT,
  buildAggregateToplineSnapshot,
  hasMeaningfulToplineData,
} from "../utils/analyticsAggregateTopline";
import {
  buildAnalyticsMetricSignature,
  buildAnalyticsScopeKey,
  readCachedAnalyticsIntelligence,
  writeCachedAnalyticsIntelligence,
} from "../utils/analyticsIntelligenceCache";

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL ?? "";

const INITIAL_LOADER_MAX_HOLD_MS = 1800;
const SOCIAL_PAGE_TITLE = "Social Analytics";
const CLIENT_SOCIAL_PROVIDER_LABEL = "Social Intelligence";
const SOCIAL_ROLLUP_INTELLIGENCE_CACHE_KEY = "analytics:intelligence:social-rollup:v2";
const SOCIAL_PRIMARY_ANALYSIS_LABEL = "OPEN PRIMARY ANALYSIS";
const SOCIAL_REPORT_BUTTON_LABEL = "OPEN SOCIAL REPORT";
const META_CONNECTED_PLATFORM = Object.freeze({
  key: "meta",
  label: "Meta",
  image: metaLogo,
});
const META_ASSET_KEYS = new Set(["facebook", "instagram"]);
export const SOCIAL_INTELLIGENCE_EXPLAINER =
  "Social Intelligence is a cross-platform live social summary for the selected advertiser, combining active delivery across connected social platforms. Best performer reflects the strongest combined delivery, engagement, efficiency, and available video-quality signals for the current reporting window.";
const SOCIAL_METRIC_SKELETON_LABELS = Object.freeze([
  "Spend",
  "Impressions",
  "Clicks",
  "Blended social CTR",
  "Best live social performer",
]);
export const SOCIAL_PLATFORM_DEFINITIONS = Object.freeze([
  /*
   * Meta is the governed parent of the Meta publisher platforms and leads the
   * social roster because Facebook and Instagram are its children, not its
   * peers. It carries excludeFromSocialRollup because the blended social
   * rollup already counts Facebook and Instagram individually — letting the
   * parent in as well would count the same delivery twice.
   */
  {
    key: "meta",
    label: "Meta",
    route: "/meta-analytics",
    image: metaLogo,
    explicitKeys: [
      "metaLink",
      "meta",
      "metaURL",
      "meta_report",
      "meta_analytics",
    ],
    liveKey: "meta",
    excludeFromSocialRollup: true,
  },
  {
    key: "facebook",
    label: "Facebook",
    route: "/facebook-analytics",
    image: facebookLogo,
    explicitKeys: [
      "facebookLink",
      "facebook",
      "facebookURL",
      "facebook_report",
      "facebook_analytics",
      "facebookAnalyticsLink",
    ],
    liveKey: "facebook",
  },
  {
    key: "instagram",
    label: "Instagram",
    route: "/instagram-analytics",
    image: instagramLogo,
    explicitKeys: [
      "instagramLink",
      "instagram",
      "instagramURL",
      "instagram_report",
      "instagram_analytics",
    ],
    liveKey: "instagram",
  },
  {
    key: "tiktok",
    label: "TikTok",
    route: "/tiktok-analytics",
    image: tiktokLogo,
    explicitKeys: [
      "tiktokLink",
      "tiktok",
      "tiktokURL",
      "tiktok_report",
      "tiktok_analytics",
    ],
    liveKey: "tiktok",
  },
  {
    key: "x",
    label: "X",
    route: "/x-analytics",
    image: xLogo,
    explicitKeys: ["xLink", "x", "xURL", "x_report", "x_analytics"],
    aliases: [
      {
        channelKey: "twitter",
        explicitKeys: [
          "twitterLink",
          "twitter",
          "twitterURL",
          "twitter_report",
          "twitter_analytics",
        ],
      },
    ],
    liveKey: "x",
  },
  {
    key: "youtube",
    label: "YouTube",
    route: "/youtube-analytics",
    image: youtubeLogo,
    explicitKeys: [
      "youtubeLink",
      "youtube",
      "youtubeURL",
      "youtube_report",
      "youtube_analytics",
    ],
    liveKey: "youtube",
  },
  {
    key: "google",
    label: "Google Search",
    tileLabel: "Google",
    route: "/google-analytics",
    image: googleLogo,
    explicitKeys: [
      "googleSearch",
      "googleSearchLink",
      "googleSearch_url",
      "googleSearchURL",
      "googleSearch_report",
      "googleLink",
      "google",
      "google_analytics",
      "google_report",
      "google_url",
      "googleURL",
    ],
    useGenericScan: false,
    liveKey: "google_search",
  },
  {
    key: "chatgpt_search",
    label: "ChatGPT Search",
    tileLabel: "ChatGPT",
    route: "/chatgpt-search-analytics",
    image: openaiLogo,
    explicitKeys: [
      "chatgptSearch",
      "chatgptSearchLink",
      "chatgptSearch_url",
      "chatgptSearchURL",
      "chatgptSearch_report",
      "chatgpt_search",
      "chatgpt_search_link",
      "chatgpt_search_url",
      "chatgpt_search_report",
    ],
    useGenericScan: false,
    liveKey: "chatgpt_search",
  },
  {
    key: "rumble",
    label: "Rumble",
    route: "/rumble-analytics",
    image: rumbleLogo,
    explicitKeys: [
      "rumbleLink",
      "rumble",
      "rumble_report",
      "rumble_url",
      "rumbleURL",
    ],
    liveKey: "rumble",
  },
  {
    key: "truthsocial",
    label: "Truth Social",
    route: "/truthsocial-analytics",
    image: truthsocialLogo,
    explicitKeys: [
      "truthSocial",
      "truthSocialLink",
      "truthSocial_url",
      "truthSocialURL",
      "truthSocial_report",
      "truthsocialLink",
      "truthsocial",
      "truth_social",
      "truth_social_link",
      "truth_social_report",
      "truthsocial_url",
      "truthsocialURL",
      "truthsocial_report",
    ],
    liveKey: "truth_social",
  },
  {
    key: "reddit",
    label: "Reddit",
    route: "/reddit-analytics",
    image: redditLogo,
    explicitKeys: [
      "redditLink",
      "reddit",
      "redditURL",
      "reddit_url",
      "reddit_report",
      "reddit_analytics",
      "redditAnalyticsLink",
    ],
    liveKey: "reddit",
  },
  {
    key: "amazon",
    label: "Amazon",
    route: "/amazon-analytics",
    image: amazonLogo,
    explicitKeys: [
      "amazonLink",
      "amazon",
      "amazonURL",
      "amazon_url",
      "amazon_report",
      "amazon_analytics",
      "amazonAnalyticsLink",
    ],
    liveKey: "amazon",
  },
  {
    key: "yahoo",
    label: "Yahoo",
    route: "/yahoo-analytics",
    image: yahooLogo,
    imageFit: "contain",
    explicitKeys: [],
    useGenericScan: false,
    // Mapping-scoped verification is shown on Yahoo Analytics, not generic OAuth badges.
    readinessInIntegrationCard: true,
    excludeFromSocialRollup: true,
  },
  {
    key: "shopify",
    label: "Shopify",
    route: "/shopify-analytics",
    image: shopifyLogo,
    explicitKeys: [
      "shopify",
      "shopifyLink",
      "shopifyURL",
      "shopify_url",
      "shopify_report",
      "shopify_analytics",
      "shopifyAnalytics",
      "shopifyAnalyticsLink",
      "shopifyCommerceReport",
      "shopifyStore",
    ],
    reportingKeys: ["shopify", "shopify_store"],
    integrationProviderKey: "SHOPIFY",
    liveKey: "shopify",
    excludeFromSocialRollup: true,
  },
  {
    key: "linkedin",
    label: "LinkedIn Ads",
    route: "/linkedin-analytics",
    image: linkedinLogo,
    explicitKeys: [
      "linkedin",
      "linkedIn",
      "linkedinAds",
      "linkedinURL",
      "linkedin_url",
      "linkedin_report",
      "linkedin_analytics",
      "linkedinAnalytics",
      "linkedinAnalyticsLink",
      "linkedInAnalyticsLink",
    ],
    reportingKeys: ["linkedin", "linkedin_ads"],
    integrationProviderKey: "LINKEDIN",
    liveKey: "linkedin",
  },
  {
    key: "community",
    label: "Community",
    route: "/community-analytics",
    image: communityManagementImage,
    explicitKeys: [
      "metricool",
      "metricoolLink",
      "metricool_url",
      "metricoolURL",
      "metricool_report",
      "metricool_analytics",
      "metricoolAnalyticsLink",
      "communityManagement",
      "communityManagementLink",
      "community_management",
      "community_manager",
      "communityManagerLink",
      "community_report",
      "community_looker",
    ],
    liveKey: null,
  },
]);

function getFirstNonEmptyAdvertiserField(source, fields = []) {
  if (!source || typeof source !== "object") return "";
  for (const field of fields) {
    const value = String(source?.[field] || "").trim();
    if (value) return value;
  }
  return "";
}

function resolveIntegrationReadinessBadge(providerStatus) {
  if (!providerStatus || typeof providerStatus !== "object") return null;
  const readinessOk =
    providerStatus?.readiness?.ok === true ||
    providerStatus?.readOnlyReady === true ||
    providerStatus?.reportingReady === true ||
    providerStatus?.lifecycle?.reportingAvailable === true;
  const connected = providerStatus?.connected === true || readinessOk;
  if (!connected) return null;
  return {
    connected,
    reportingReady: readinessOk,
    label: readinessOk ? "REPORTING READY" : "CONNECTED",
  };
}

function renderSocialMetricSkeletonCards() {
  return SOCIAL_METRIC_SKELETON_LABELS.map((label) => (
    <div
      key={`social-skeleton-${label}`}
      className="social-analytics-provider-metric-card social-analytics-provider-metric-card-skeleton"
      aria-hidden="true"
    >
      <div className="social-analytics-provider-metric-label">{label}</div>
      <div className="social-analytics-provider-skeleton-line social-analytics-provider-skeleton-line-value" />
      <div className="social-analytics-provider-skeleton-line social-analytics-provider-skeleton-line-meta" />
    </div>
  ));
}

export function buildSocialRollupSnapshotFromTopline(topline) {
  if (!topline || typeof topline !== "object") return null;
  const window = readToplineReportingWindow(topline);
  if (!window.effective) return null;
  const metrics = [
    topline.spend
      ? {
          id: "spend",
          label: "Spend",
          value: topline.spend,
          meta: "Client-billed spend across live social platforms",
        }
      : null,
    topline.impressions
      ? {
          id: "impressions",
          label: "Impressions",
          value: topline.impressions,
          meta: "Live social reach",
        }
      : null,
    topline.clicks
      ? {
          id: "clicks",
          label: "Clicks",
          value: topline.clicks,
          meta: "Live social click volume",
        }
      : null,
    topline.ctr
      ? {
          id: "ctr",
          label: "Blended social CTR",
          value: topline.ctr,
          meta: "Clicks divided by impressions across live social platforms",
        }
      : null,
  ].filter(Boolean);

  return {
    metrics,
    metadata: {
      reportingWindowStatus: "complete",
      effectiveReportingWindow: window.effective,
      windowLabel: window.label,
      requestedReportingWindows: window.requested ? [{ ...window.requested, label: window.requestedLabel }] : [],
      excludedWindowPlatforms: [],
      eligiblePlatformCount: 0,
      includedPlatformKeys: [],
      includedPlatformLabels: [],
      topPlatform: "",
      topPlatformKey: "",
      topPlatformSourceTier: topline.raw?.snapshotSourceTier || "canonical_cached",
      topPlatformSourceLabel: topline.raw?.snapshotSourceLabel || "Live social summary",
    },
    toplinesByPlatform: {},
  };
}

export function resolveSocialMetricLoadingState({
  currentAdvertiserRaw,
  socialToplinesReady,
  prioritizedMetricCards = [],
}) {
  return Boolean(
    currentAdvertiserRaw &&
      !socialToplinesReady &&
      (!Array.isArray(prioritizedMetricCards) || prioritizedMetricCards.length === 0)
  );
}

function getSocialPlatformRuntimeKey(platform) {
  return String(platform?.liveKey || platform?.key || "").trim().toLowerCase();
}

function isMetaAssetPlatform(platform) {
  return META_ASSET_KEYS.has(getSocialPlatformRuntimeKey(platform));
}

function resolvePrimaryConnectedPlatformDisplay({
  connectedSocialPlatforms = [],
  primaryAnalysisPlatform = null,
  primaryReportPlatform = null,
} = {}) {
  const configuredPlatform = primaryReportPlatform || primaryAnalysisPlatform || connectedSocialPlatforms[0] || null;
  if (!configuredPlatform) return null;

  const metaAssetPlatforms = connectedSocialPlatforms.filter(isMetaAssetPlatform);
  const metaAssetKeys = new Set(metaAssetPlatforms.map(getSocialPlatformRuntimeKey));
  const hasMetaIntegrationBoundary =
    metaAssetPlatforms.some((platform) => platform?.hasLiveProviderSignal) ||
    (metaAssetKeys.has("facebook") && metaAssetKeys.has("instagram"));

  if (hasMetaIntegrationBoundary) {
    return {
      ...META_CONNECTED_PLATFORM,
      sourcePlatform: configuredPlatform,
      connectedAssets: metaAssetPlatforms,
      isIntegrationBoundary: true,
    };
  }

  return configuredPlatform;
}

export function resolveSocialPlatformCardState({
  aggregateToplineMetadata = {},
  connectedSocialPlatforms = [],
  primaryAnalysisPlatform = null,
  primaryReportPlatform = null,
  socialDeliveryHydrating = false,
  socialToplines = {},
  topPerformingPlatform = null,
}) {
  const toplineEntries = Object.values(socialToplines || {}).filter(Boolean);
  const topPlatformSourceTier = String(aggregateToplineMetadata?.topPlatformSourceTier || "")
    .trim()
    .toLowerCase();
  const hasLiveComparativeData =
    topPlatformSourceTier === "live_direct" &&
    Boolean(aggregateToplineMetadata?.topPlatform) &&
    Boolean(topPerformingPlatform);
  const hasFallbackComparativeData =
    !hasLiveComparativeData &&
    Boolean(topPlatformSourceTier) &&
    Boolean(aggregateToplineMetadata?.topPlatform) &&
    Boolean(topPerformingPlatform);
  const hasAnyMeaningfulLiveData = toplineEntries.some((entry) => hasMeaningfulToplineData(entry));
  const pacingStatuses = toplineEntries
    .map((entry) => String(entry?.raw?.pacingStatus || entry?.pacingStatus || "").trim().toLowerCase())
    .filter(Boolean);
  const isPreFlight = pacingStatuses.includes("pre-flight");
  const configuredPlatform = resolvePrimaryConnectedPlatformDisplay({
    connectedSocialPlatforms,
    primaryAnalysisPlatform,
    primaryReportPlatform,
  });

  if (hasLiveComparativeData) {
    return {
      label: "Best live social performer",
      value: topPerformingPlatform.label,
      meta: "Leading social platform by delivery, engagement, efficiency, and available video-quality signals for the current window.",
      image: topPerformingPlatform.image,
    };
  }

  if (hasFallbackComparativeData) {
    return {
      label: "Best available social performer",
      value: topPerformingPlatform.label,
      meta: "Current social ranking reflects the strongest available delivery, engagement, efficiency, and video-quality signals for this analysis window.",
      image: topPerformingPlatform.image,
    };
  }

  if (socialDeliveryHydrating) {
    return {
      label: "Best live social performer",
      value: "Evaluating",
      meta: "Evaluating live social performance...",
      image: null,
    };
  }

  if (isPreFlight) {
    return {
      label: "Campaign Status",
      value: "Pre-Live",
      meta: "Social platform ranking will appear after the flight begins and live delivery data is available.",
      image: null,
    };
  }

  if (!hasAnyMeaningfulLiveData && configuredPlatform) {
    const isMetaIntegrationBoundary = configuredPlatform.isIntegrationBoundary;
    return {
      label: "Primary Connected Platform",
      value: configuredPlatform.label,
      meta: isMetaIntegrationBoundary
        ? "Meta integration is connected. Connected assets are shown separately. Comparative ranking will appear once delivery data is available."
        : primaryReportPlatform
        ? "Connected social report is configured. Comparative ranking will appear once delivery data is available."
        : "Configured social platform in scope. Comparative ranking will appear once delivery data is available.",
      image: configuredPlatform.image,
    };
  }

  return null;
}

const SocialAnalytics = () => {
  const navigate = useNavigate();
  const [aggregateToplineMetrics, setAggregateToplineMetrics] = useState([]);
  const [aggregateToplineMetadata, setAggregateToplineMetadata] = useState({});
  const [socialToplines, setSocialToplines] = useState({});
  const [socialToplinesReady, setSocialToplinesReady] = useState(false);
  const [socialToplinesRefreshing, setSocialToplinesRefreshing] = useState(false);
  const [socialToplinesRefreshError, setSocialToplinesRefreshError] = useState("");
  const [metaProviderState, setMetaProviderState] = useState(null);
  const [availableSocialPlatformKeys, setAvailableSocialPlatformKeys] = useState([]);
  const [integrationReadinessByPlatform, setIntegrationReadinessByPlatform] = useState({});
  const [showInitialLoader, setShowInitialLoader] = useState(true);
  const [loaderSafetyElapsed, setLoaderSafetyElapsed] = useState(false);
  const [showSocialReport, setShowSocialReport] = useState(false);
  const [socialViewerStatus, setSocialViewerStatus] = useState("idle");
  const [socialViewerError, setSocialViewerError] = useState("");
  const initialLoaderDismissedRef = useRef(false);
  const advertiserScopeEpochRef = useRef(0);

  const getToken = () => localStorage.getItem("authToken");

  /**
   * 🔹 Channel-link detector
   * - explicit keys first
   * - optional generic scan across keys for legacy naming
   * - IMPORTANT: for Google, we disable generic scan so google_drive_folder_url
   *   does NOT count as "connected".
   */
  const hasChannelLink = (
    advRaw,
    channelKey,
    explicitKeys = [],
    options = {}
  ) => {
    const { useGenericScan = true } = options;

    if (!advRaw) return false;

    for (const key of explicitKeys) {
      const val = advRaw[key];
      if (typeof val === "string" && val.trim() !== "") return true;
    }

    if (!useGenericScan) return false; // ✅ hard stop when disabled
    if (!channelKey) return false;

    const channel = channelKey.toLowerCase();

    return Object.entries(advRaw).some(([key, value]) => {
      if (typeof value !== "string") return false;
      const k = String(key).toLowerCase();

      const matchesChannel = k.includes(channel);
      const matchesType = ["link", "url", "report", "looker", "analytics"].some(
        (frag) => k.includes(frag)
      );

      return matchesChannel && matchesType && value.trim() !== "";
    });
  };

  const clearAdvertiserScopedPresentationState = useCallback(() => {
    advertiserScopeEpochRef.current += 1;
    setAggregateToplineMetrics([]);
    setAggregateToplineMetadata({});
    setSocialToplines({});
    setSocialToplinesReady(false);
    setSocialToplinesRefreshing(false);
    setSocialToplinesRefreshError("");
    setMetaProviderState(null);
    setAvailableSocialPlatformKeys([]);
    setIntegrationReadinessByPlatform({});
    setShowSocialReport(false);
    setSocialViewerStatus("idle");
    setSocialViewerError("");
  }, []);

  const {
    selectedAdvertiserId,
    visibleAdvertisers,
    currentAdvertiser,
    catalogReady,
    accessReady,
    loading: advertiserCatalogLoading,
    selectorDisabled,
    selectorEmpty,
    modalOpen: advertiserSelectorOpen,
    openSelector: openAdvertiserSelector,
    closeSelector: closeAdvertiserSelector,
    handleAdvertiserSelect,
  } = useGovernedAdvertiserCatalog({
    apiBase: API_BASE_URL,
    role: "client",
    onAdvertiserChange: clearAdvertiserScopedPresentationState,
  });

  const currentAdvertiserRaw = currentAdvertiser?.raw ?? null;
  const governedAdvertiserId = currentAdvertiser?.id ?? null;
  const governedAdvertiserName = currentAdvertiser?.name || "";
  const advertiserVertical = useMemo(
    () => normalizeAdvertiserVertical(currentAdvertiserRaw?.vertical),
    [currentAdvertiserRaw]
  );

  const { timeframe, advertiserFlightRange } =
    useResolvedAnalyticsTimeframe(governedAdvertiserId, currentAdvertiserRaw);

  useEffect(() => {
    let cancelled = false;
    const scopeEpoch = advertiserScopeEpochRef.current;
    const isCurrentScope = () =>
      !cancelled && scopeEpoch === advertiserScopeEpochRef.current;

    const loadMetaProviderState = async () => {
      if (!governedAdvertiserId || !API_BASE_URL) {
        if (isCurrentScope()) {
          setMetaProviderState(null);
          setAvailableSocialPlatformKeys([]);
        }
        return;
      }

      const token = getToken();
      if (!token) {
        if (isCurrentScope()) {
          setMetaProviderState(null);
          setAvailableSocialPlatformKeys([]);
        }
        return;
      }

      try {
        const params = new URLSearchParams({
          advertiserId: String(governedAdvertiserId),
          datePreset: String(timeframe?.datePreset || "last_30d"),
        });
        if (timeframe?.start) params.set("startDate", String(timeframe.start));
        if (timeframe?.end) params.set("endDate", String(timeframe.end));
        const response = await fetch(`${API_BASE_URL}/api/optimus/platforms?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
        });

        if (!response.ok) {
          if (isCurrentScope()) {
            setMetaProviderState(null);
            setAvailableSocialPlatformKeys([]);
          }
          return;
        }

        const payload = await response.json().catch(() => ({}));
        if (isCurrentScope()) {
          setMetaProviderState(payload?.providerStates?.meta || null);
          setAvailableSocialPlatformKeys(
            Array.isArray(payload?.tabs)
              ? payload.tabs
                  .map((tab) => String(tab?.id || "").trim().toLowerCase())
                  .filter((id) => id && id !== "all")
              : []
          );
        }
      } catch (error) {
        if (isCurrentScope()) {
          console.error("[SocialAnalytics] failed to load Meta provider state", error);
          setMetaProviderState(null);
          setAvailableSocialPlatformKeys([]);
        }
      }
    };

    loadMetaProviderState();
    return () => {
      cancelled = true;
    };
  }, [governedAdvertiserId, timeframe?.datePreset, timeframe?.end, timeframe?.start]);

  useEffect(() => {
    let cancelled = false;
    const scopeEpoch = advertiserScopeEpochRef.current;
    const isCurrentScope = () =>
      !cancelled && scopeEpoch === advertiserScopeEpochRef.current;

    const loadIntegrationReadiness = async () => {
      if (!governedAdvertiserId || !API_BASE_URL) {
        if (isCurrentScope()) setIntegrationReadinessByPlatform({});
        return;
      }

      const token = getToken();
      if (!token) {
        if (isCurrentScope()) setIntegrationReadinessByPlatform({});
        return;
      }

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/integrations/${governedAdvertiserId}?ts=${Date.now()}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            cache: "no-store",
          }
        );
        if (!response.ok) {
          if (isCurrentScope()) setIntegrationReadinessByPlatform({});
          return;
        }
        const payload = await response.json().catch(() => ({}));
        if (isCurrentScope()) {
          setIntegrationReadinessByPlatform({
            shopify: resolveIntegrationReadinessBadge(payload?.SHOPIFY),
            linkedin: resolveIntegrationReadinessBadge(payload?.LINKEDIN),
          });
        }
      } catch (error) {
        if (isCurrentScope()) {
          console.error("[SocialAnalytics] failed to load Shopify/LinkedIn readiness", error);
          setIntegrationReadinessByPlatform({});
        }
      }
    };

    loadIntegrationReadiness();
    return () => {
      cancelled = true;
    };
  }, [governedAdvertiserId]);

  const metaReportingReady = useMemo(
    () => Boolean(metaProviderState?.reportingAvailable || metaProviderState?.connected),
    [metaProviderState]
  );

  const availableSocialPlatformSet = useMemo(
    () => new Set((availableSocialPlatformKeys || []).map((key) => String(key || "").trim().toLowerCase())),
    [availableSocialPlatformKeys]
  );

  const socialPlatformEntries = useMemo(
    () =>
      SOCIAL_PLATFORM_DEFINITIONS.map((platform) => {
        const primaryReportUrl = getFirstNonEmptyAdvertiserField(
          currentAdvertiserRaw,
          platform.explicitKeys
        );
        const aliasMatch = Array.isArray(platform.aliases)
          ? platform.aliases.find((alias) =>
              hasChannelLink(
                currentAdvertiserRaw,
                alias.channelKey,
                alias.explicitKeys || [],
                { useGenericScan: alias.useGenericScan !== false }
              )
            )
          : null;
        const aliasReportUrl = aliasMatch
          ? getFirstNonEmptyAdvertiserField(currentAdvertiserRaw, aliasMatch.explicitKeys || [])
          : "";
        const hasLinkSignal =
          hasChannelLink(
            currentAdvertiserRaw,
            platform.key,
            platform.explicitKeys,
            { useGenericScan: platform.useGenericScan !== false }
          ) || Boolean(aliasMatch);
        const platformLookupKeys = [
          platform.liveKey,
          platform.key,
          ...(Array.isArray(platform.reportingKeys) ? platform.reportingKeys : []),
          ...(Array.isArray(platform.aliases)
            ? platform.aliases.map((alias) => alias?.channelKey)
            : []),
        ]
          .map((value) => String(value || "").trim().toLowerCase())
          .filter(Boolean);
        const hasSnapshotSignal = platformLookupKeys.some((key) =>
          availableSocialPlatformSet.has(key)
        );
        // The Meta parent is the Meta integration boundary itself, so the same
        // provider signal that lights up Facebook and Instagram lights it up.
        const isMetaLivePlatform =
          platform.liveKey === "meta" ||
          platform.liveKey === "facebook" ||
          platform.liveKey === "instagram";
        const hasLiveProviderSignal = isMetaLivePlatform ? metaReportingReady : false;
        const readinessBadge =
          integrationReadinessByPlatform[platform.key] ||
          integrationReadinessByPlatform[
            String(platform.integrationProviderKey || "").trim().toLowerCase()
          ] ||
          null;
        const hasReadinessSignal = Boolean(readinessBadge?.connected);
        const connected = !platform.readinessInIntegrationCard &&
          (hasLiveProviderSignal || hasLinkSignal || hasSnapshotSignal || hasReadinessSignal);

        return {
          ...platform,
          connected,
          hasLinkSignal,
          hasSnapshotSignal,
          hasLiveProviderSignal,
          hasReadinessSignal,
          statusBadgeLabel: readinessBadge?.label || (connected ? "CONNECTED" : ""),
          reportUrl: primaryReportUrl || aliasReportUrl || "",
        };
      }),
    [availableSocialPlatformSet, currentAdvertiserRaw, integrationReadinessByPlatform, metaReportingReady]
  );

  const connectedSocialPlatforms = useMemo(
    () => socialPlatformEntries.filter((platform) => platform.connected),
    [socialPlatformEntries]
  );

  const eligibleAnalysisPlatforms = useMemo(
    () =>
      connectedSocialPlatforms
        .filter((platform) => platform.liveKey && !platform.excludeFromSocialRollup)
        .map((platform) => ({
          key: platform.liveKey,
          label: platform.label,
        })),
    [connectedSocialPlatforms]
  );
  const socialAggregateScopeKey = useMemo(
    () =>
      governedAdvertiserId
        ? buildAnalyticsScopeKey({
            page: "social-rollup",
            advertiserId: governedAdvertiserId,
            timeframe,
            advertiserFlightRange,
            platforms: eligibleAnalysisPlatforms.map((platform) => platform.key).sort(),
          })
        : "",
    [advertiserFlightRange, eligibleAnalysisPlatforms, governedAdvertiserId, timeframe]
  );

  useEffect(() => {
    let cancelled = false;
    const scopeEpoch = advertiserScopeEpochRef.current;
    const isCurrentScope = () =>
      !cancelled && scopeEpoch === advertiserScopeEpochRef.current;

    const run = async () => {
      if (!governedAdvertiserId || !eligibleAnalysisPlatforms.length) {
        if (isCurrentScope()) {
          setAggregateToplineMetrics([]);
          setAggregateToplineMetadata({});
          setSocialToplines({});
          setSocialToplinesReady(true);
          setSocialToplinesRefreshing(false);
          setSocialToplinesRefreshError("");
        }
        return;
      }

      const cached = readCachedAnalyticsIntelligence(SOCIAL_ROLLUP_INTELLIGENCE_CACHE_KEY, {
        scopeKey: socialAggregateScopeKey,
      });
      const cachedPayload = cached?.payload || null;

      if (isCurrentScope()) {
        if (cachedPayload) {
          setAggregateToplineMetrics(cachedPayload.metrics || []);
          setAggregateToplineMetadata(cachedPayload.metadata || {});
          setSocialToplines(cachedPayload.toplinesByPlatform || {});
          setSocialToplinesReady(true);
        } else {
          setAggregateToplineMetrics([]);
          setAggregateToplineMetadata({});
          setSocialToplines({});
          setSocialToplinesReady(false);
        }
        setSocialToplinesRefreshing(true);
        setSocialToplinesRefreshError("");
      }

      if (!cachedPayload) {
        try {
          const canonicalTopline = await getToplinePerformance({
            advertiserId: governedAdvertiserId,
            timeframe,
            advertiserFlightRange,
            platformKey: "all",
          });
          const canonicalSnapshot = buildSocialRollupSnapshotFromTopline(canonicalTopline);
          if (isCurrentScope() && canonicalSnapshot?.metrics?.length) {
            setAggregateToplineMetrics(canonicalSnapshot.metrics);
            setAggregateToplineMetadata(canonicalSnapshot.metadata);
            setSocialToplines(canonicalSnapshot.toplinesByPlatform);
            setSocialToplinesReady(true);
          }
        } catch (error) {
          if (isCurrentScope()) {
            console.error("[SocialAnalytics][Topline] canonical seed failed", error);
          }
        }
      }

      const platformResults = await Promise.all(
        eligibleAnalysisPlatforms.map(async (platform) => {
          try {
            const topline = await getToplinePerformance({
              advertiserId: governedAdvertiserId,
              timeframe,
              advertiserFlightRange,
              platformKey: platform.key,
            });
            return [platform.key, topline];
          } catch (error) {
            if (isCurrentScope()) {
              console.error("[SocialAnalytics][Topline] platform fetch failed", platform.key, error);
            }
            return [platform.key, null];
          }
        })
      );

      if (!isCurrentScope()) return;

      const toplinesByPlatform = Object.fromEntries(platformResults);
      setSocialToplines(toplinesByPlatform);

      const snapshot = buildAggregateToplineSnapshot({
        pageLabel: SOCIAL_PAGE_TITLE,
        platforms: eligibleAnalysisPlatforms,
        toplines: toplinesByPlatform,
        advertiserVertical,
        rankingContext: SOCIAL_INTELLIGENCE_RANKING_CONTEXT,
      });

      const nextMetrics = snapshot.metrics || [];
      setAggregateToplineMetrics(nextMetrics);
      setAggregateToplineMetadata(snapshot.metadata || {});
      if (!nextMetrics.length) {
        setSocialToplinesRefreshError("No common reporting window with usable measurements is available.");
      }
      setSocialToplinesReady(true);
      setSocialToplinesRefreshing(false);
      if (nextMetrics.length) setSocialToplinesRefreshError("");
      writeCachedAnalyticsIntelligence(SOCIAL_ROLLUP_INTELLIGENCE_CACHE_KEY, {
        scopeKey: socialAggregateScopeKey,
        metricSignature: buildAnalyticsMetricSignature({
          metrics: nextMetrics,
          metadata: snapshot.metadata || {},
          sources: Object.entries(toplinesByPlatform).map(([key, topline]) => ({
            key,
            metricSignature: topline?.raw?.metricSignature || "",
            sourceFreshness:
              topline?.raw?.sourceUpdatedAt ||
              topline?.raw?.latestIngestedAt ||
              topline?.raw?.snapshotUpdatedAt ||
              "",
          })),
        }),
        payload: {
          metrics: nextMetrics,
          metadata: snapshot.metadata || {},
          toplinesByPlatform,
        },
      });
    };

    run().catch((error) => {
      if (isCurrentScope()) {
        console.error("[SocialAnalytics][Topline] refresh failed", error);
        setSocialToplinesRefreshing(false);
        setSocialToplinesRefreshError("Live social refresh is delayed. Showing the latest available KPI values.");
        setSocialToplinesReady(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    advertiserVertical,
    advertiserFlightRange,
    eligibleAnalysisPlatforms,
    governedAdvertiserId,
    socialAggregateScopeKey,
    timeframe,
  ]);

  const connectedPlatformLabels = useMemo(
    () => connectedSocialPlatforms.map((platform) => platform.label),
    [connectedSocialPlatforms]
  );

  const reportReadyPlatforms = useMemo(
    () => connectedSocialPlatforms.filter((platform) => platform.reportUrl),
    [connectedSocialPlatforms]
  );

  const primaryAnalysisPlatform = connectedSocialPlatforms[0] || null;
  const primaryReportPlatform = reportReadyPlatforms[0] || null;
  const primarySocialReportUrl = primaryReportPlatform?.reportUrl || "";
  const normalizedPrimarySocialReport = useMemo(
    () => normalizeAnalyticsEmbedUrl(primarySocialReportUrl),
    [primarySocialReportUrl]
  );

  const socialReportDescription = useMemo(() => {
    if (!governedAdvertiserId) {
      return "Select an advertiser to launch a primary social analysis surface or open a mapped social report.";
    }
    if (primaryReportPlatform?.reportUrl) {
      return "";
    }
    if (primaryAnalysisPlatform) {
      return `Open ${primaryAnalysisPlatform.label} Analytics to inspect the strongest currently mapped social surface for this advertiser.`;
    }
    return "No connected social report surface is available yet. Once a platform is mapped, the launch controls will appear here.";
  }, [governedAdvertiserId, primaryAnalysisPlatform, primaryReportPlatform]);

  const connectedPillLabel = useMemo(() => {
    if (!governedAdvertiserId) return "Awaiting Advertiser";
    if (connectedSocialPlatforms.length) {
      return "Connected";
    }
    return "Not Connected";
  }, [connectedSocialPlatforms.length, governedAdvertiserId]);

  useEffect(() => {
    setShowSocialReport(false);
    setSocialViewerStatus("idle");
    setSocialViewerError("");
  }, [governedAdvertiserId, primarySocialReportUrl]);

  useEffect(() => {
    if (!showSocialReport || socialViewerStatus !== "loading") return undefined;

    const timeoutId = window.setTimeout(() => {
      setSocialViewerStatus("error");
      setSocialViewerError("Embedded report could not be displayed here.");
    }, 10000);

    return () => window.clearTimeout(timeoutId);
  }, [showSocialReport, socialViewerStatus]);

  const topPerformingPlatform = useMemo(() => {
    const topPlatformKey = String(aggregateToplineMetadata?.topPlatformKey || "")
      .trim()
      .toLowerCase();
    const topPlatformLabel = String(aggregateToplineMetadata?.topPlatform || "").trim();

    if (topPlatformKey) {
      return (
        connectedSocialPlatforms.find(
          (platform) => String(platform.liveKey || platform.key || "").trim().toLowerCase() === topPlatformKey
        ) ||
        socialPlatformEntries.find(
          (platform) => String(platform.liveKey || platform.key || "").trim().toLowerCase() === topPlatformKey
        ) ||
        null
      );
    }

    if (!topPlatformLabel) return null;

    const normalizedLabel = topPlatformLabel.toLowerCase();
    return (
      connectedSocialPlatforms.find(
        (platform) =>
          String(platform.label || "").trim().toLowerCase() === normalizedLabel ||
          String(platform.tileLabel || "").trim().toLowerCase() === normalizedLabel
      ) ||
      socialPlatformEntries.find(
        (platform) =>
          String(platform.label || "").trim().toLowerCase() === normalizedLabel ||
          String(platform.tileLabel || "").trim().toLowerCase() === normalizedLabel
      ) ||
      null
    );
  }, [
    aggregateToplineMetadata,
    connectedSocialPlatforms,
    socialPlatformEntries,
  ]);

  const socialDeliveryHydrating = useMemo(
    () =>
      Boolean(
        currentAdvertiserRaw &&
          eligibleAnalysisPlatforms.length &&
          (!socialToplinesReady || socialToplinesRefreshing)
      ),
    [
      currentAdvertiserRaw,
      eligibleAnalysisPlatforms.length,
      socialToplinesReady,
      socialToplinesRefreshing,
    ]
  );

  const socialPlatformCardState = useMemo(
    () =>
      resolveSocialPlatformCardState({
        aggregateToplineMetadata,
        connectedSocialPlatforms,
        primaryAnalysisPlatform,
        primaryReportPlatform,
        socialDeliveryHydrating,
        socialToplines,
        topPerformingPlatform,
      }),
    [
      aggregateToplineMetadata,
      connectedSocialPlatforms,
      primaryAnalysisPlatform,
      primaryReportPlatform,
      socialDeliveryHydrating,
      socialToplines,
      topPerformingPlatform,
    ]
  );

  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;

    const diagnostics = aggregateToplineMetadata?.topPlatformDiagnostics;
    if (!diagnostics?.platforms?.length) return;

    console.debug("[SocialAnalytics][TopPlatformDiagnostics]", diagnostics);
  }, [aggregateToplineMetadata]);

  const prioritizedMetricCards = useMemo(() => {
    const metricLookup = new Map(
      (aggregateToplineMetrics || []).map((metric) => [
        String(metric?.label || "").trim().toLowerCase(),
        metric,
      ])
    );

    const cards = ["spend", "impressions", "clicks", "ctr"]
      .map((label) => metricLookup.get(label))
      .filter(Boolean)
      .map((metric) => ({
        type: "metric",
        id: metric.id || metric.label,
        label: metric.label,
        value: metric.value,
        meta: metric.meta,
      }));

    if (socialPlatformCardState) {
      cards.push({
        type: "platform-state",
        id: `platform-state-${String(socialPlatformCardState.value || "unknown")
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")}`,
        label: socialPlatformCardState.label,
        value: socialPlatformCardState.value,
        meta: socialPlatformCardState.meta,
        image: socialPlatformCardState.image,
      });
    }

    return cards;
  }, [aggregateToplineMetrics, socialPlatformCardState]);

  const socialMetricsLoading = useMemo(
    () =>
      resolveSocialMetricLoadingState({
        currentAdvertiserRaw,
        socialToplinesReady,
        prioritizedMetricCards,
      }),
    [currentAdvertiserRaw, prioritizedMetricCards, socialToplinesReady]
  );

  const showSocialMetricArea = useMemo(
    () => socialMetricsLoading || prioritizedMetricCards.length > 0,
    [prioritizedMetricCards.length, socialMetricsLoading]
  );

  const advertiserSelectionResolved = useMemo(() => {
    if (!catalogReady || !accessReady) return false;
    if (governedAdvertiserId) return true;
    return visibleAdvertisers.length !== 1;
  }, [
    accessReady,
    catalogReady,
    governedAdvertiserId,
    visibleAdvertisers.length,
  ]);

  const initialShellReady = useMemo(() => {
    if (!catalogReady || !accessReady) return false;
    if (!advertiserSelectionResolved) return false;
    return true;
  }, [accessReady, advertiserSelectionResolved, catalogReady]);

  const initialDisplayReady = useMemo(() => {
    if (!initialShellReady) return false;

    if (!currentAdvertiserRaw) {
      return true;
    }

    return socialToplinesReady;
  }, [currentAdvertiserRaw, initialShellReady, socialToplinesReady]);

  const shouldDismissInitialLoader = useMemo(() => {
    if (initialDisplayReady) return true;
    if (!loaderSafetyElapsed) return false;
    return initialShellReady;
  }, [initialDisplayReady, initialShellReady, loaderSafetyElapsed]);

  useEffect(() => {
    if (initialLoaderDismissedRef.current) return undefined;

    const timer = window.setTimeout(() => {
      setLoaderSafetyElapsed(true);
    }, INITIAL_LOADER_MAX_HOLD_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (initialLoaderDismissedRef.current) return undefined;
    if (!shouldDismissInitialLoader) {
      setShowInitialLoader(true);
      return undefined;
    }

    let raf1 = 0;
    let raf2 = 0;

    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        initialLoaderDismissedRef.current = true;
        setShowInitialLoader(false);
      });
    });

    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
    };
  }, [shouldDismissInitialLoader]);

  const socialPageAwareness = useMemo(
    () => ({
      routeKey: "/social-analytics",
      pageTitle: SOCIAL_PAGE_TITLE,
      pageType: "analytics",
      advertiserId: governedAdvertiserId,
      advertiserName: governedAdvertiserName,
      timeframe,
      platformKey: "social_rollup",
      platformLabel: "Social",
      toplineScope: "aggregate",
      pageCapabilities: [
        "social performance overview",
        "cross-platform comparison",
        "live platform aggregate read",
        "channel readiness context",
      ],
      aggregateToplineMetrics,
      metadata: {
        eligiblePlatformCount: aggregateToplineMetadata?.eligiblePlatformCount || 0,
        includedPlatformKeys: aggregateToplineMetadata?.includedPlatformKeys || [],
        includedPlatformLabels: aggregateToplineMetadata?.includedPlatformLabels || [],
        topPlatform: aggregateToplineMetadata?.topPlatform || "",
        effectiveReportingWindow: aggregateToplineMetadata?.effectiveReportingWindow || null,
        reportingCalendar: aggregateToplineMetadata?.reportingCalendar || null,
        requestedReportingWindows: aggregateToplineMetadata?.requestedReportingWindows || [],
        excludedWindowPlatforms: aggregateToplineMetadata?.excludedWindowPlatforms || [],
        connectedPlatformLabels,
      },
    }),
    [
      aggregateToplineMetadata,
      aggregateToplineMetrics,
      connectedPlatformLabels,
      governedAdvertiserId,
      governedAdvertiserName,
      timeframe,
    ]
  );

  usePageAwareness(socialPageAwareness);

  return (
    <div className="core-dashboard social-analytics-page-container">
      <Loading active={showInitialLoader} />
      <div className="core-dashboard-content social-analytics-page-content cortex-page-intro-shell">
        <CphereHeaderLogo
          alt="Powered by Logo"
          className="social-analytics-poweredby-logo"
          onClick={() => navigate("/client")}
        />

        <div className="cphere-governed-hero-selector-stack">
          <div className="social-analytics-hero-container">
            <div className="social-analytics-hero-pill">
              <span className="social-analytics-hero-pill-label cphere-hero-pill-wordmark">Social</span>
              <span className="social-analytics-hero-pill-tag cphere-hero-pill-tag">Analytics</span>
            </div>
            <div className="social-analytics-hero-shimmer-bar" />
          </div>

          <div className="cphere-governed-advertiser-selector-stage">
            <AdvertiserIdentityTrigger
              advertiser={currentAdvertiser}
              advertiserName={governedAdvertiserName}
              apiBase={API_BASE_URL}
              disabled={selectorDisabled}
              loading={advertiserCatalogLoading}
              empty={selectorEmpty}
              modalOpen={advertiserSelectorOpen}
              onClick={openAdvertiserSelector}
            />
          </div>
        </div>

        {advertiserSelectorOpen && visibleAdvertisers.length ? (
          <GovernedAdvertiserSelectorModal
            advertisers={visibleAdvertisers}
            selectedAdvertiserId={selectedAdvertiserId}
            selectedAdvertiserName={governedAdvertiserName}
            apiBase={API_BASE_URL}
            onSelect={handleAdvertiserSelect}
            onClose={closeAdvertiserSelector}
          />
        ) : null}

        <div className="social-analytics-tile-grid-outer">
          <div className="social-analytics-tile-grid">
            {socialPlatformEntries.map((platform) => (
              <button
                key={platform.key}
                className="social-analytics-tile"
                onClick={() => navigate(platform.route)}
                style={{ backgroundImage: `url(${platform.image})`, ...(platform.imageFit ? { backgroundSize: platform.imageFit } : {}) }}
                type="button"
              >
                {platform.connected ? (
                  <span className="social-analytics-connected-badge">
                    {platform.statusBadgeLabel || "CONNECTED"}
                  </span>
                ) : null}
                <span className="social-analytics-tile-label">
                  {platform.tileLabel || platform.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="social-analytics-provider-panel">
          <div className="social-analytics-provider-panel-copy">
            <div className="social-analytics-provider-header">
              <div className="social-analytics-provider-header-stack">
                <span className="social-analytics-provider-logo-ring">
                  <img
                    src={campaignManagementImage}
                    alt="Social"
                    className="social-analytics-provider-logo social-analytics-provider-logo-campaignmanagement"
                  />
                </span>
                <div className="social-analytics-provider-text">
                  <div className="social-analytics-provider-title-stack">
                    <div className="social-analytics-provider-title">
                      {CLIENT_SOCIAL_PROVIDER_LABEL}
                    </div>
                    <div
                      className={`social-analytics-provider-connected-pill ${
                        connectedSocialPlatforms.length
                          ? "social-analytics-provider-connected-pill-ready"
                          : "social-analytics-provider-connected-pill-pending"
                      }`}
                    >
                      {connectedPillLabel}
                    </div>
                  </div>
                  <div className="social-analytics-provider-explainer">
                    {SOCIAL_INTELLIGENCE_EXPLAINER}
                  </div>
                </div>
              </div>
            </div>

            {connectedPlatformLabels.length ? (
              <div className="social-analytics-provider-asset-group">
                <div className="social-analytics-provider-asset-label">
                  Connected Assets:
                </div>
                <div className="social-analytics-provider-chip-row">
                  {connectedPlatformLabels.map((label) => (
                    <span key={label} className="social-analytics-provider-chip">
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}

            {showSocialMetricArea ? (
              <div
                className={`social-analytics-provider-metric-shell ${
                  socialMetricsLoading
                    ? "social-analytics-provider-metric-shell-loading"
                    : "social-analytics-provider-metric-shell-ready"
                }`}
                aria-busy={socialMetricsLoading}
              >
                {socialMetricsLoading ? (
                  <div className="social-analytics-provider-metric-loading-copy">
                    Loading live social analytics...
                  </div>
                ) : null}
                {!socialMetricsLoading && socialToplinesRefreshing ? (
                  <div className="social-analytics-provider-metric-loading-copy">
                    Refreshing live social analytics...
                  </div>
                ) : null}
                {!socialMetricsLoading && socialToplinesRefreshError ? (
                  <div className="social-analytics-provider-metric-loading-copy">
                    {socialToplinesRefreshError}
                  </div>
                ) : null}

                {!socialMetricsLoading && aggregateToplineMetadata.windowLabel ? (
                  <div className="social-analytics-provider-note" data-reporting-window="effective">
                    Reporting window: {aggregateToplineMetadata.windowLabel}
                    {/* Only a calendar the answers stated — never the portal's default. */}
                    {reportingTimeZoneStatement(aggregateToplineMetadata.reportingCalendar?.responseTimeZone) ? (
                      <span data-reporting-time-zone={aggregateToplineMetadata.reportingCalendar.responseTimeZone}>
                        {" · "}{reportingTimeZoneStatement(aggregateToplineMetadata.reportingCalendar.responseTimeZone)}
                      </span>
                    ) : null}
                  </div>
                ) : null}
                {!socialMetricsLoading && sourceCalendarQualification({ reportingCalendar: { normalization: aggregateToplineMetadata.reportingCalendar?.sourceCalendars } }) ? (
                  <div className="social-analytics-provider-note" data-source-calendar-qualification="true">
                    {sourceCalendarQualification({ reportingCalendar: { normalization: aggregateToplineMetadata.reportingCalendar?.sourceCalendars } })}
                  </div>
                ) : null}
                {!socialMetricsLoading && aggregateCalendarQualification(aggregateToplineMetadata.reportingCalendar) ? (
                  <div className="social-analytics-provider-note" data-calendar-not-stated="true">
                    {aggregateCalendarQualification(aggregateToplineMetadata.reportingCalendar)}
                  </div>
                ) : null}
                {!socialMetricsLoading && (aggregateToplineMetadata.requestedReportingWindows || []).map(window => (
                  <div key={`${window.start}/${window.end}`} className="social-analytics-provider-note" data-reporting-window="requested">
                    Requested window: {window.label}
                  </div>
                ))}
                {!socialMetricsLoading && (aggregateToplineMetadata.excludedWindowPlatforms || []).map(platform => (
                  <div key={platform.key} className="social-analytics-provider-note" data-reporting-window="excluded">
                    {platform.label} excluded from this total: {platform.windowLabel || "reporting window unavailable"}.
                  </div>
                ))}
                <div
                  className={`social-analytics-provider-metric-grid ${
                    socialMetricsLoading
                      ? "social-analytics-provider-metric-grid-loading"
                      : "social-analytics-provider-metric-grid-ready"
                  }`}
                >
                  {socialMetricsLoading
                    ? renderSocialMetricSkeletonCards()
                    : prioritizedMetricCards.map((card) => (
                        <div
                          key={card.id}
                          className="social-analytics-provider-metric-card social-analytics-provider-metric-card-ready"
                        >
                          <div className="social-analytics-provider-metric-label">{card.label}</div>
                          {card.image ? (
                            <div className="social-analytics-provider-platform-value">
                              {card.image ? (
                                <span className="social-analytics-provider-platform-logo-shell" aria-hidden="true">
                                  <img
                                    src={card.image}
                                    alt=""
                                    className="social-analytics-provider-platform-logo"
                                    draggable={false}
                                  />
                                </span>
                              ) : null}
                              <span className="social-analytics-provider-platform-name">{card.value}</span>
                            </div>
                          ) : (
                            <div className="social-analytics-provider-metric-value">{card.value}</div>
                          )}
                          <div className="social-analytics-provider-metric-meta">{card.meta}</div>
                        </div>
                      ))}
                </div>
              </div>
            ) : null}

            {socialReportDescription ? (
              <div className="social-analytics-provider-note">
                {socialReportDescription}
              </div>
            ) : null}

            <div className="social-analytics-provider-actions">
              <button
                type="button"
                className="social-analytics-provider-button"
                disabled={!primaryAnalysisPlatform}
                onClick={() => primaryAnalysisPlatform && navigate(primaryAnalysisPlatform.route)}
              >
                {SOCIAL_PRIMARY_ANALYSIS_LABEL}
              </button>

              {primaryReportPlatform?.reportUrl ? (
                <button
                  type="button"
                  className="social-analytics-provider-link social-analytics-provider-link-report"
                  onClick={() => {
                    if (!primaryReportPlatform?.reportUrl) return;
                    if (showSocialReport) {
                      setShowSocialReport(false);
                      setSocialViewerStatus("idle");
                      setSocialViewerError("");
                      return;
                    }
                    setShowSocialReport(true);
                    setSocialViewerStatus("loading");
                    setSocialViewerError("");
                  }}
                >
                  {showSocialReport ? "CLOSE REPORT" : SOCIAL_REPORT_BUTTON_LABEL}
                </button>
              ) : null}
            </div>
          </div>
        </div>

      </div>

      <AnalyticsReportViewer
        isOpen={showSocialReport}
        onClose={() => {
          setShowSocialReport(false);
          setSocialViewerStatus("idle");
          setSocialViewerError("");
        }}
        ariaLabel="Social report viewer"
        title="Social Analytics"
        embedUrl={normalizedPrimarySocialReport.embedUrl || primarySocialReportUrl}
        loadingImage={reportLoadingIcon}
        viewerStatus={socialViewerStatus}
        viewerError={socialViewerError}
        onLoad={() => {
          setSocialViewerStatus("ready");
          setSocialViewerError("");
        }}
        onError={() => {
          setSocialViewerStatus("error");
          setSocialViewerError("Embedded report could not be displayed here.");
        }}
        unavailableMessage={
          governedAdvertiserId
            ? `No social report is configured for ${governedAdvertiserName}.`
            : "Choose an advertiser to resolve the linked social reporting surface."
        }
      />

      <div className="one-back-button" onClick={() => navigate(-1)}>
        <img src={reverseArrowImage} alt="Back" />
      </div>
    </div>
  );
};

export default SocialAnalytics;
